import { useCallback, useRef } from 'react';
import { useLocalStorage } from '@mantine/hooks';
import { useRoomContext } from '@livekit/components-react';

import type { REACTIONS } from './reactions';

export type Sound = (typeof REACTIONS)[number]['sound'] | 'raised-hand' | 'lobby-request';

type SoundSettings = { reactions: boolean; raisedHand: boolean };

const DEFAULT_SOUND_SETTINGS: SoundSettings = { reactions: true, raisedHand: true };
const SOUND_VOLUME = 0.4;
const SOUND_BURST_GAP_MS = 500;

export function useSoundSettings() {
    const [stored, setStored] = useLocalStorage<Partial<SoundSettings>>({
        key: 'meet.sounds',
        defaultValue: DEFAULT_SOUND_SETTINGS,
        getInitialValueInEffect: false,
    });
    const setReactions = useCallback(
        (reactions: boolean) => {
            setStored((current) => ({ ...current, reactions }));
        },
        [setStored]
    );
    const setRaisedHand = useCallback(
        (raisedHand: boolean) => {
            setStored((current) => ({ ...current, raisedHand }));
        },
        [setStored]
    );
    return { ...DEFAULT_SOUND_SETTINGS, ...stored, setReactions, setRaisedHand };
}

export function useSoundPlayer() {
    const room = useRoomContext();
    const audioRef = useRef(new Map<Sound, HTMLAudioElement>());
    const lastHeardAtRef = useRef(new Map<Sound, number>());

    return useCallback(
        (sound: Sound) => {
            const now = Date.now();
            const lastHeardAt = lastHeardAtRef.current.get(sound) ?? 0;
            lastHeardAtRef.current.set(sound, now);
            if (now - lastHeardAt < SOUND_BURST_GAP_MS) return;

            let audio = audioRef.current.get(sound);
            if (!audio) {
                audio = new Audio();
                const extension = audio.canPlayType('audio/ogg; codecs=opus') ? 'opus' : 'mp3';
                audio.src = `/sounds/reactions-${sound}.${extension}`;
                audio.volume = SOUND_VOLUME;
                audioRef.current.set(sound, audio);
            }
            const element = audio;
            const sinkId = room.getActiveDevice('audiooutput');
            const routed =
                sinkId && 'setSinkId' in element && element.sinkId !== sinkId ? element.setSinkId(sinkId) : Promise.resolve();
            routed
                .then(() => {
                    element.currentTime = 0;
                    return element.play();
                })
                .catch((error: unknown) => {
                    const ignorable =
                        error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError');
                    if (!ignorable) console.warn('Reaction sound failed', error);
                });
        },
        [room]
    );
}
